import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import {Platform} from 'react-native';
import {BASE_URL, AUTHORIZATION_TOKEN, ENDPOINTS} from '../../config';

import {
  ProductType,
  BannerType,
  CarouselType,
  TagType,
  CategoryType,
  PromocodeType,
  ReviewType,
  ProductAddon,
  ProductAddonOption,
} from '../../types';

const isBrowserEnvironment = (): boolean => {
  if (Platform.OS === 'web') {
    return true;
  }

  if (typeof globalThis === 'undefined') {
    return false;
  }

  const globalScope = globalThis as {window?: unknown; document?: unknown};

  return Boolean(globalScope.window && globalScope.document);
};

type StoreAccessProduct = {
  id?: string;
  _id?: string;
  store_id?: string;
  name: string;
  price: number;
  oldPrice?: number | null;
  old_price?: number | null;
  weight?: number | string | null;
  kcal?: number | string | null;
  images?: string[];
  image?: string | null;
  image_url?: string | null;
  description?: string | null;
  dietaryPreferences?: string[];
  rating?: string | number | null;
  isRecommended?: boolean;
  is_recommended?: boolean;
  isNew?: boolean;
  is_new?: boolean;
  isHot?: boolean;
  is_hot?: boolean;
  menu?: string[];
  category?: string[];
  colors?: {name: string; code: string}[];
  addons?: StoreAccessAddon[];
};

type StoreAccessAddonOption = {
  id?: string | null;
  name?: string | null;
  price?: number | null;
  active?: boolean | null;
};

type StoreAccessAddon = {
  id?: string | null;
  name?: string | null;
  minUse?: number | null;
  maxUse?: number | null;
  platformId?: string | null;
  plateformId?: string | null;
  options?: StoreAccessAddonOption[] | null;
};

type StoreAccessMenu = {
  id?: string;
  _id?: string;
  store_id?: string;
  name: string;
  image?: string | null;
  image_url?: string | null;
};

type StoreAccessCarousel = {
  id?: string;
  _id?: string;
  store_id?: string;
  banner?: string | null;
  banner_url?: string | null;
  image?: string | null;
  image_url?: string | null;
  viewport?: string | null;
};

type StoreAccessReview = {
  id?: string;
  _id?: string;
  store_id?: string;
  name: string;
  avatar?: string | null;
  avatar_url?: string | null;
  rating: number | string;
  date: string;
  comment?: string | null;
  created_at?: string;
  updated_at?: string;
};

type StoreAccessPromocode = {
  id?: string;
  _id?: string;
  name?: string | null;
  description?: string | null;
  discount: number;
  code: string;
  starts_at?: string | null;
  expires_at?: string | null;
};

type StoreAccessOrderItemAddon = {
  addon_id: string | null;
  option_id: string | null;
  quantity: number;
};

type StoreAccessOrderRequest = {
  customer: {
    name: string;
    email: string | null;
    phone: string | null;
    address: string | null;
    latitude?: number | null;
    longitude?: number | null;
  };
  items: {
    id: string;
    name: string | null;
    price: number | null;
    quantity: number;
    image: string | null;
    addons?: StoreAccessOrderItemAddon[];
  }[];
  note: string | null;
  payment_type: string | null;
  currency: string;
  idempotency_key?: string;
};

type StoreAccessOrderResponse = {
  id: string;
  orderId: string;
  status: string;
  placedAt: string;
};

const mapId = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  return value;
};

const buildImageUrl = (path?: string | null): string | null => {
  if (!path) {
    return null;
  }

  const trimmedPath = path.trim();

  if (!trimmedPath) {
    return null;
  }

  let relativePath = trimmedPath;

  if (/^https?:\/\//i.test(trimmedPath)) {
    try {
      const url = new URL(trimmedPath);
      relativePath = url.pathname || '';
    } catch {
      relativePath = trimmedPath;
    }
  } else if (trimmedPath.startsWith(BASE_URL)) {
    relativePath = trimmedPath.slice(BASE_URL.length);
  }

  const normalizedPath = relativePath.replace(/^\/+/u, '');

  if (!normalizedPath) {
    return null;
  }

  const hasPublicPrefix = normalizedPath.startsWith('public/');

  const basePath = hasPublicPrefix
    ? normalizedPath
    : `public/${normalizedPath}`;

  return `${BASE_URL}/${basePath}`;
};

const buildImageArray = (images?: (string | null | undefined)[]): string[] =>
  (images ?? [])
    .map((image) => buildImageUrl(image))
    .filter((image): image is string => Boolean(image));

const mapAddonOption = (option: StoreAccessAddonOption): ProductAddonOption => {
  return {
    id: option.id ?? null,
    name: option.name ?? 'Option',
    price: Number.isFinite(Number(option.price)) ? Number(option.price) : 0,
    active: option.active ?? true,
  };
};

const mapAddon = (addon: StoreAccessAddon): ProductAddon => {
  const minUse = Number.isFinite(Number(addon.minUse))
    ? Number(addon.minUse)
    : 0;
  const rawMaxUse = Number.isFinite(Number(addon.maxUse))
    ? Number(addon.maxUse)
    : null;

  const options = (addon.options ?? [])
    .map(mapAddonOption)
    .filter((option) => option.active);

  return {
    id: addon.id ?? addon.name ?? 'addon',
    name: addon.name ?? 'Addon',
    minUse: minUse < 0 ? 0 : minUse,
    maxUse: rawMaxUse !== null && rawMaxUse < 0 ? null : rawMaxUse,
    plateformId: addon.platformId ?? addon.plateformId ?? null,
    options,
  };
};

const mapProduct = (product: StoreAccessProduct): ProductType => {
  const images = buildImageArray([
    ...(product.images ?? []),
    product.image_url ?? product.image ?? null,
  ]);
  const ratingValue = product.rating ?? null;
  const backendId = product._id ?? product.id ?? '';
  const addons: ProductAddon[] = (product.addons ?? [])
    .map(mapAddon)
    .filter((addon) => addon.options.length > 0);

  const rawOldPrice = product.oldPrice ?? product.old_price ?? null;
  const normalizedOldPrice =
    typeof rawOldPrice === 'number' && rawOldPrice > product.price
      ? rawOldPrice
      : null;

  return {
    id: product.id ?? backendId,
    backendId,
    storeId: product.store_id,
    name: product.name,
    description: product.description ?? null,
    price: product.price,
    oldPrice: normalizedOldPrice,
    image: images[0] ?? '',
    images,
    weight: product.weight ?? null,
    calories: product.kcal ?? null,
    dietaryPreferences: product.dietaryPreferences ?? [],
    rating:
      ratingValue === null || ratingValue === undefined
        ? null
        : Number.isNaN(Number(ratingValue))
        ? null
        : Number(ratingValue),
    isRecommended: product.isRecommended ?? product.is_recommended ?? false,
    isNew: product.isNew ?? product.is_new ?? false,
    isHot: product.isHot ?? product.is_hot ?? false,
    menu: product.menu ?? product.category ?? [],
    colors: product.colors ?? [],
    addons,
  };
};

const mapMenu = (menu: StoreAccessMenu): CategoryType => {
  return {
    id: menu.id ?? menu._id ?? '',
    storeId: menu.store_id,
    name: menu.name,
    imageUrl: buildImageUrl(menu.image_url ?? menu.image ?? null),
  };
};

const mapCarousel = (carousel: StoreAccessCarousel): CarouselType => {
  const bannerUrl = buildImageUrl(carousel.banner_url ?? null);
  const imageUrl = buildImageUrl(
    carousel.banner_url ?? carousel.image_url ?? carousel.image ?? null,
  );
  const normalizedViewport = (() => {
    if (typeof carousel.viewport !== 'string') {
      return null;
    }

    const viewportValue = carousel.viewport.toLowerCase().trim();

    if (
      viewportValue === 'desktop' ||
      viewportValue === 'mobile' ||
      viewportValue === 'pad'
    ) {
      return viewportValue as CarouselType['viewport'];
    }

    return null;
  })();
  return {
    id: carousel.id ?? carousel._id ?? '',
    storeId: carousel.store_id,
    banner: carousel.banner ?? null,
    bannerUrl,
    imageUrl,
    viewport: normalizedViewport,
  };
};

const mapReview = (review: StoreAccessReview): ReviewType => {
  const avatarUrl = buildImageUrl(review.avatar_url ?? review.avatar ?? null);

  const parsedRating = Number(review.rating ?? 0);

  return {
    id: review.id ?? review._id ?? '',
    storeId: review.store_id,
    name: review.name,
    avatarUrl,
    rating: Number.isNaN(parsedRating) ? 0 : parsedRating,
    date: review.date,
    comment: review.comment ?? null,
  };
};

const mapPromocode = (promocode: StoreAccessPromocode): PromocodeType => {
  return {
    id: promocode.id ?? promocode._id ?? mapId(promocode.code) ?? '',
    name: promocode.name ?? null,
    description: promocode.description ?? null,
    discount: promocode.discount,
    code: promocode.code,
    starts_at: promocode.starts_at ?? null,
    expires_at: promocode.expires_at ?? null,
  };
};

export const apiSlice = createApi({
  reducerPath: 'apiSlice',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
    ...(isBrowserEnvironment() ? {credentials: 'omit' as const} : {}),
    prepareHeaders: (headers) => {
      headers.set('authorization', `Bearer ${AUTHORIZATION_TOKEN}`);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getProducts: builder.query<ProductType[], void>({
      query: () => ENDPOINTS.get.products,
      transformResponse: (response: StoreAccessProduct[]) =>
        response?.map(mapProduct) ?? [],
    }),
    getBanners: builder.query<{banners: BannerType[]}, void>({
      query: () => ENDPOINTS.get.banners,
    }),
    getCarousel: builder.query<CarouselType[], void>({
      query: () => ENDPOINTS.get.carousel,
      transformResponse: (response: StoreAccessCarousel[]) =>
        response?.map(mapCarousel) ?? [],
    }),
    getCategories: builder.query<CategoryType[], void>({
      query: () => ENDPOINTS.get.menus,
      transformResponse: (response: StoreAccessMenu[]) =>
        response?.map(mapMenu) ?? [],
    }),
    getReviews: builder.query<ReviewType[], void>({
      query: () => ENDPOINTS.get.reviews,
      transformResponse: (response: StoreAccessReview[]) =>
        response?.map(mapReview) ?? [],
    }),
    getTags: builder.query<{tags: TagType[]}, void>({
      query: () => ENDPOINTS.get.tags,
    }),
    validatePromocode: builder.mutation<
      {promocode: PromocodeType},
      {promocode: string}
    >({
      query: ({promocode}) => ({
        url: ENDPOINTS.get.discount,
        method: 'GET',
        params: {promocode},
      }),
      transformResponse: (response: {promocode: StoreAccessPromocode}) => ({
        promocode: mapPromocode(response.promocode),
      }),
    }),
    fetchPromocode: builder.mutation<PromocodeType, {code: string}>({
      query: ({code}) => ({
        url: ENDPOINTS.post.promocode,
        method: 'POST',
        body: {code},
      }),
      transformResponse: (response: StoreAccessPromocode) =>
        mapPromocode(response),
    }),
    createOrder: builder.mutation<
      StoreAccessOrderResponse,
      {payload: StoreAccessOrderRequest; idempotencyKey: string}
    >({
      query: ({payload, idempotencyKey}) => {
        const isWeb = isBrowserEnvironment();
        const requestPayload: StoreAccessOrderRequest = isWeb
          ? {...payload, idempotency_key: idempotencyKey}
          : payload;

        const request: {
          url: string;
          method: 'POST';
          body: StoreAccessOrderRequest;
          credentials?: 'omit';
          headers?: Record<string, string>;
        } = {
          url: ENDPOINTS.post.order,
          method: 'POST',
          body: requestPayload,
        };

        if (isWeb) {
          request.credentials = 'omit';
        } else {
          request.headers = {'Idempotency-Key': idempotencyKey};
        }

        return request;
      },
    }),
  }),
});

export const {
  useGetTagsQuery,
  useGetBannersQuery,
  useGetReviewsQuery,
  useGetProductsQuery,
  useGetCarouselQuery,
  useGetCategoriesQuery,
  useValidatePromocodeMutation,
  useFetchPromocodeMutation,
  useCreateOrderMutation,
} = apiSlice;

export default apiSlice.reducer;
